'use client'
import { useEffect, useState } from 'react'

import { Footer } from '../Footer'
import { Navbar } from '../Navbar'
import { Filters } from './Filters'
import { AdviceCell } from './table/AdviceCell'
import { TagsCell } from './table/TagsCell'
import { TopicsCell } from './table/TopicsCell'
import { Category, Tag } from './types'
import { flattenAdvices } from './utils'

interface Props {
  categories: Category[]
  footerData: any
}
const translations = {
  en: {
    allAdvice: 'All Advice',
    advice: 'Advice',
    topics: 'Topics',
    tags: 'Tags',
  },
  nb: {
    allAdvice: 'Alle råd',
    advice: 'Råd',
    topics: 'Emner',
    tags: 'Tagger',
  },
  fi: {
    allAdvice: 'Kaikki ohjeet',
    advice: 'Ohjeet',
    topics: 'Aiheet',
    tags: 'Tunnisteet',
  },
}

export default function AllAdvicesBody(props: Props) {
  const { categories, footerData } = props
  const language = categories.length > 0 ? categories[0].language : 'en'
  const t =
    translations[language as keyof typeof translations] || translations.en
  const allAdvices = flattenAdvices(categories).filter(
    (advice) => advice.language === language,
  )
  const colours = [
    'blue',
    'green',
    'red',
    'yellow',
    'purple',
    'indigo',
    'pink',
    'gray',
  ]
  const categoryColours = categories.map((category, i) => ({
    title: category.title,
    colour: colours[i],
  }))
  const [filteredAdvices, setFilteredAdvices] = useState(allAdvices)
  const [checkedTopics, setCheckedTopics] = useState<string[]>([])
  const [checkedTags, setCheckedTags] = useState<string[]>([])

  const allTopics: string[][] = [
    ...new Set(
      categories.flatMap((category) =>
        category.topics
          ? category.topics.map((topic) => [topic.title, category.title])
          : [],
      ),
    ),
  ]

  useEffect(() => {
    if (checkedTopics.length === 0 && checkedTags.length === 0) {
      setFilteredAdvices(allAdvices)
    } else {
      setFilteredAdvices(
        allAdvices.filter(
          (advice) =>
            (checkedTopics.length === 0
              ? true
              : checkedTopics.every(
                  (checkedTopic) =>
                    advice.topics &&
                    advice.topics
                      .map((adviceTopic) => adviceTopic.title)
                      .includes(checkedTopic),
                )) &&
            (checkedTags.length === 0
              ? true
              : checkedTags.every(
                  (checkedTag) =>
                    advice.tags &&
                    advice.tags.map((tag) => tag.title).includes(checkedTag),
                )),
        ),
      )
    }
  }, [checkedTopics, checkedTags, allAdvices])

  const allTags = [
    ...new Set(
      allAdvices
        .flatMap((advice) => advice.tags)
        .filter((tag) => tag !== undefined && tag !== null)
        .map((t) => t.title),
    ),
  ]

  return (
    <>
      <Navbar
        locale={language}
        slugTranslations={{
          en: 'all-advice',
          nb: 'alle-rad',
          fi: 'kaikki-ohjeet',
        }}
      />
      <main className="mx-auto w-full max-w-[var(--max-width)] px-4 py-16 md:px-8">
        <div className="mb-8 border-b border-b-blue-100 pb-4">
          <h1 className="mb-2 text-2xl font-semibold md:text-3xl lg:text-4xl">
            {t.allAdvice}
          </h1>
        </div>
        <div className="mb-8 flex gap-4 flex-col md:flex-row">
          <Filters
            language={language}
            setFilteredAdvices={setFilteredAdvices}
            allAdvices={allAdvices}
            allTags={allTags}
            categories={categories}
            checkedTags={checkedTags}
            setCheckedTags={setCheckedTags}
            checkedTopics={checkedTopics}
            setCheckedTopics={setCheckedTopics}
          />
          <section className="order-2 md:order-none">
            <table>
              <thead>
                <tr>
                  <th className="w-4/12 text-left p-2">{t.advice}</th>
                  <th className="w-6/12 text-left p-2">{t.topics}</th>
                  {allTags.length > 0 && (
                    <th className="w-2/12 text-left p-2">{t.tags}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {[...filteredAdvices]
                  .sort(function (a, b) {
                    if (a.title < b.title) {
                      return -1
                    }
                    if (a.title > b.title) {
                      return 1
                    }
                    return 0
                  })
                  .map(
                    (advice: {
                      slug: string
                      language: string
                      title: string
                      description: string
                      tags: Tag[]
                      topics: {
                        title: string
                        language: string
                        slug: string
                      }[]
                      categories: {
                        title: string
                        language: string
                        slug: string
                      }[]
                    }) => (
                      <tr
                        key={advice.slug}
                        className="border-b border-t border-gray-300"
                      >
                        <td className="whitespace-normal p-2 break-words max-w-[50vw]">
                          <AdviceCell advice={advice} />
                        </td>
                        <td className="p-2">
                          <TopicsCell
                            advice={advice}
                            allTopics={allTopics}
                            categoryColours={categoryColours}
                          />
                        </td>
                        {allTags.length > 0 && (
                          <td className="p-2">
                            <TagsCell advice={advice} language={language} />
                          </td>
                        )}
                      </tr>
                    ),
                  )}
              </tbody>
            </table>
          </section>
        </div>
      </main>
      <Footer data={footerData} />
    </>
  )
}
